/**
 * Preferences questionnaire session: answers, cursor, answerQuestion → Leave → leaveQuestionnaire.
 *
 * Intern cheat sheet:
 * 1. updateDraftAnswer — multi-select / highlight only
 * 2. answerQuestion — confirm answer; switch on result.type
 * 3. allDone → leaveQuestionnaire(result.leave)
 * 4. Next on last step with no new answer → finishFromDraftAnswers() then leaveQuestionnaire
 * 5. Always use leave.answers; animations/nav never invent maps
 * 6. Back out → cancelAnswering (also resetCursorToStart)
 * 7. Leave/exit/cancel resetCursorToStart (safety net — hub routes by status)
 * 8. Full-flow answers via usePreferencesSessionHydration → ApplyHydration
 *
 * Belongs here: draft/committed, cursor, answerQuestion/leaveQuestionnaire, edit/exit.
 * Does not belong here: API fetch (provider), pure flow rules, page handlers, or FullFlow hydrate policy.
 */
import { useCallback, useEffect, useMemo, useReducer } from 'react'
import type {
  FundPreferenceType,
  PreferencesAnswersMap,
  PreferencesQuestionApi,
  PreferenceQuestionnaireDataResponse
} from '../setup/types'
import type { PreferencesQuestionnaireMap } from '../../question-utils/preferencesMappers'
import {
  getPreferencesAnswersAfterAdd,
  getPreferencesNextAfterAnswer,
  getVisibleQuestionsForPreferenceType as getVisibleQuestionsForType,
  PreferencesNextAfterAnswerTargetType
} from './flowRules'
import {
  EXPERT_REVIEW_CAME_FROM,
  type ExpertReviewCameFrom,
  useExpertReviewNavigation
} from '../../navigation/expertReviewNavigation'
import { SlideDirection } from '../../shared/transitions/DirectionalSlideSwap'
import { useContentTransition } from '../../shared/transitions/useContentTransition'
import type { ExpertReviewStatus } from '../../providers/ExpertReviewStatusContext'
import { usePreferencesSessionHydration } from './usePreferencesSessionHydration'
import {
  initialPreferencesSessionState,
  PreferencesConfirmOutcome,
  PreferencesSessionActionType,
  preferencesSessionReducer
} from './preferencesSessionReducer'

export interface UsePreferencesStateArgs {
  preferenceTypeIdFromPathParam: string | undefined
  preferenceTypes: FundPreferenceType[]
  questionnaireMap: PreferencesQuestionnaireMap
  rawQuestionnaireData: PreferenceQuestionnaireDataResponse | undefined
  /** /status slice for this questionnaire (same shape as strategy). */
  expertReviewStatus: ExpertReviewStatus
}

/**
 * Where to send the user after answering is finished.
 * Built into the Leave plan at AllDone — leaveQuestionnaire does not re-guess.
 */
export enum PreferencesWhereToGo {
  Review = 'review',
  Back = 'back'
}

/**
 * Bundle from answerQuestion(allDone) / finishFromDraftAnswers → leaveQuestionnaire.
 * answers is the finished map (already committed in the reducer); prefs review submits later.
 */
export type PreferencesLeave = {
  whereToGo: PreferencesWhereToGo
  answers: PreferencesAnswersMap
}

/**
 * Discriminator for PreferencesAnswerResult — page-flow switches on this, never raw strings.
 */
export enum PreferencesAnswerResultType {
  NextQuestion = 'next_question',
  PreferenceTypeDone = 'preference_type_done',
  AllDone = 'all_done',
  Ignored = 'ignored'
}

/** What answerQuestion tells the page to do next — page-flow switches on `type`. */
export type PreferencesAnswerResult =
  | { type: PreferencesAnswerResultType.NextQuestion }
  | { type: PreferencesAnswerResultType.PreferenceTypeDone }
  | { type: PreferencesAnswerResultType.AllDone; leave: PreferencesLeave }
  | { type: PreferencesAnswerResultType.Ignored }

/**
 * Preferences questionnaire session — one long-lived instance under the provider.
 */
export function usePreferencesQuestionnaire({
  preferenceTypeIdFromPathParam,
  preferenceTypes,
  questionnaireMap,
  rawQuestionnaireData,
  expertReviewStatus
}: UsePreferencesStateArgs) {
  const {
    goToPreferencesReview,
    goToPreferencesQuestionnaire,
    goToPreferencesTypeEdit,
    goBackFromPreferencesQuestionnaire
  } = useExpertReviewNavigation()

  const [session, dispatch] = useReducer(
    preferencesSessionReducer,
    initialPreferencesSessionState
  )
  const { draftAnswers, committedAnswers, preferenceTypeIndex, questionIndex, hasReachedReviewOnce, committedAnswersVersion, lastSubmittedAnswersVersion } =
    session

  const isAnswersVersionOutOfSync = committedAnswersVersion !== lastSubmittedAnswersVersion

  usePreferencesSessionHydration({
    preferenceTypeIdFromPathParam,
    rawQuestionnaireData,
    committedAnswers,
    expertReviewStatus,
    dispatch
  })

  const { slideDirection, startTransition } = useContentTransition()

  // Single-type edit route: move cursor to the type in the URL (PreparePreferenceTypeEdit seeds answers)
  useEffect(() => {
    if (!preferenceTypeIdFromPathParam?.trim() || preferenceTypes.length === 0) return
    const typeIndex = preferenceTypes.findIndex(
      (preferenceType) => preferenceType.id === preferenceTypeIdFromPathParam
    )
    if (typeIndex >= 0) {
      dispatch({
        type: PreferencesSessionActionType.MoveCursor,
        preferenceTypeIndex: typeIndex,
        questionIndex: 0
      })
    }
  }, [preferenceTypeIdFromPathParam, preferenceTypes])

  const currentPreferenceType = useMemo(
    () =>
      preferenceTypes.length > 0 && preferenceTypeIndex < preferenceTypes.length
        ? preferenceTypes[preferenceTypeIndex]
        : null,
    [preferenceTypes, preferenceTypeIndex]
  )

  const getVisibleQuestionsForPreferenceType = useCallback(
    (
      preferenceTypeId: string,
      answersOverride?: PreferencesAnswersMap
    ): PreferencesQuestionApi[] => {
      return getVisibleQuestionsForType(
        questionnaireMap,
        preferenceTypeId,
        answersOverride ?? draftAnswers
      )
    },
    [questionnaireMap, draftAnswers]
  )

  const currentPreferenceTypeId = currentPreferenceType?.id
  const visibleQuestionsForCurrentType = useMemo(
    () =>
      currentPreferenceTypeId
        ? getVisibleQuestionsForPreferenceType(currentPreferenceTypeId)
        : [],
    [currentPreferenceTypeId, getVisibleQuestionsForPreferenceType]
  )

  const currentQuestion = useMemo((): PreferencesQuestionApi | null => {
    return visibleQuestionsForCurrentType[questionIndex] ?? null
  }, [visibleQuestionsForCurrentType, questionIndex])

  /** Back/Next without confirming — slide first, then MoveCursor (no draft commit). */
  const goToQuestion = useCallback(
    (
      nextPreferenceTypeIndex: number,
      nextQuestionIndex: number,
      direction: SlideDirection
    ) => {
      startTransition(direction)
      dispatch({
        type: PreferencesSessionActionType.MoveCursor,
        preferenceTypeIndex: nextPreferenceTypeIndex,
        questionIndex: nextQuestionIndex
      })
    },
    [startTransition]
  )

  const clearAnswer = useCallback((questionId: string) => {
    dispatch({ type: PreferencesSessionActionType.ClearQuestionAnswer, questionId })
  }, [])

  /** Selection / multi-select only — updates draftAnswers; does not move forward or commit. */
  const updateDraftAnswer = useCallback(
    (questionId: string, value: string | number | string[]) => {
      dispatch({
        type: PreferencesSessionActionType.UpdateDraftAnswer,
        questionId,
        value,
        questionnaireMap
      })
    },
    [questionnaireMap]
  )

  /**
   * Destination baked into the Leave plan at AllDone / finishFromDraftAnswers.
   * Single-type edit pops back; full flow replaces to review.
   */
  function buildWhereToGo(): PreferencesWhereToGo {
    if (preferenceTypeIdFromPathParam?.trim()) return PreferencesWhereToGo.Back
    return PreferencesWhereToGo.Review
  }

  /**
   * Execute a Leave plan from AllDone / finishFromDraftAnswers.
   * Prefs does not POST here — review later submits committedAnswers (already set on finish).
   * Always reset cursor after leave — provider stays mounted (mirrors strategy).
   */
  function leaveQuestionnaire(leave: PreferencesLeave) {
    if (leave.whereToGo === PreferencesWhereToGo.Back) {
      goBackFromPreferencesQuestionnaire()
      resetCursorToStart()
      return
    }
    goToPreferencesReview({
      cameFrom: EXPERT_REVIEW_CAME_FROM.PREFERENCES_QUESTIONNAIRE,
      replace: true
    })
    resetCursorToStart()
  }

  /**
   * User confirmed an answer on the current question.
   *
   * Flow:
   * 1. Peek nextAnswers + visibility (same helpers the reducer will use)
   * 2. Ask flowRules where to go
   * 3. Dispatch AnswerQuestion with matching outcome
   * 4. Return a simple result for page-flow
   *
   * Single-type edit treats “this type” as the last type so finishing it yields allDone.
   */
  function answerQuestion(
    questionId: string,
    value: string | number | string[],
    options?: { isSingleTypeEdit?: boolean }
  ): PreferencesAnswerResult {
    if (!currentPreferenceType || !currentQuestion) {
      return { type: PreferencesAnswerResultType.Ignored }
    }

    const nextAnswers = getPreferencesAnswersAfterAdd(
      draftAnswers,
      questionId,
      value,
      questionnaireMap
    )
    const visibleAfterAnswer = getVisibleQuestionsForPreferenceType(
      currentPreferenceType.id,
      nextAnswers
    )
    // Single-type edit: finishing this type ends the session (pop back), not “next type”
    const isLastPreferenceType =
      options?.isSingleTypeEdit === true ||
      preferenceTypeIndex >= Math.max(0, preferenceTypes.length - 1)

    const advanceTarget = getPreferencesNextAfterAnswer({
      preferenceTypeIndex,
      questionIndex,
      currentQuestionId: currentQuestion.id,
      visibleQuestions: visibleQuestionsForCurrentType,
      visibleAfterAnswer,
      isLastPreferenceType
    })

    if (advanceTarget.type === PreferencesNextAfterAnswerTargetType.NextQuestion) {
      startTransition(SlideDirection.Forward)
      dispatch({
        type: PreferencesSessionActionType.AnswerQuestion,
        questionId,
        value,
        questionnaireMap,
        outcome: {
          kind: PreferencesConfirmOutcome.NextQuestion,
          preferenceTypeIndex,
          questionIndex: advanceTarget.questionIndex
        }
      })
      return { type: PreferencesAnswerResultType.NextQuestion }
    }

    // PreferenceTypeDone: stay in questionnaire — slide to next type (no Leave yet)
    if (advanceTarget.type === PreferencesNextAfterAnswerTargetType.NextPreferenceType) {
      startTransition(SlideDirection.Forward)
      dispatch({
        type: PreferencesSessionActionType.AnswerQuestion,
        questionId,
        value,
        questionnaireMap,
        outcome: {
          kind: PreferencesConfirmOutcome.PreferenceTypeDone,
          nextPreferenceTypeIndex: advanceTarget.nextPreferenceTypeIndex
        }
      })
      return { type: PreferencesAnswerResultType.PreferenceTypeDone }
    }

    // AllDone: reducer promotes draft → committed and keeps cursor (Leave plan navigates away)
    dispatch({
      type: PreferencesSessionActionType.AnswerQuestion,
      questionId,
      value,
      questionnaireMap,
      outcome: { kind: PreferencesConfirmOutcome.AllDone }
    })

    return {
      type: PreferencesAnswerResultType.AllDone,
      leave: { whereToGo: buildWhereToGo(), answers: nextAnswers }
    }
  }

  /**
   * Commit current draft without a new AnswerQuestion (Next on last multi-select step).
   * Answer is already in draft — we only promote draft → committed and return a Leave plan.
   * Caller must leaveQuestionnaire(returned leave).
   */
  function finishFromDraftAnswers(): PreferencesLeave {
    const answers = { ...draftAnswers }
    dispatch({ type: PreferencesSessionActionType.CommitDraftAnswers })
    return { whereToGo: buildWhereToGo(), answers }
  }

  /** Reset draftAnswers to committedAnswers — drops unconfirmed highlights. */
  function discardDraftAnswers() {
    dispatch({ type: PreferencesSessionActionType.DiscardDraft })
  }

  /** Back out of answering: discard draft, pop route, reset cursor (provider stays mounted). */
  const cancelAnswering = useCallback(() => {
    discardDraftAnswers()
    goBackFromPreferencesQuestionnaire()
    resetCursorToStart()
  }, [goBackFromPreferencesQuestionnaire])

  /** Invalid answering route: discard draft and replace to preferences review. */
  const returnToReview = useCallback(() => {
    discardDraftAnswers()
    goToPreferencesReview({
      cameFrom: EXPERT_REVIEW_CAME_FROM.PREFERENCES_REVIEW,
      replace: true
    })
    resetCursorToStart()
  }, [goToPreferencesReview])

  /** Clear one preference type from committed and open its edit route. */
  const beginEditOne = useCallback(
    (preferenceTypeId: string) => {
      const typeIndex = preferenceTypes.findIndex(
        (preferenceType) => preferenceType.id === preferenceTypeId
      )
      dispatch({
        type: PreferencesSessionActionType.PreparePreferenceTypeEdit,
        preferenceTypeId,
        preferenceTypeIndex: typeIndex >= 0 ? typeIndex : 0,
        questionnaireMap
      })
      startTransition(SlideDirection.Forward)
      goToPreferencesTypeEdit({ preferenceTypeId })
    },
    [goToPreferencesTypeEdit, questionnaireMap, preferenceTypes, startTransition]
  )

  /** Discard draft only and open full questionnaire (committed stays until AllDone / CommitDraft). */
  const beginEditAll = useCallback(
    (options?: { cameFrom?: ExpertReviewCameFrom }) => {
      dispatch({
        type: PreferencesSessionActionType.BeginEditAll,
        questionnaireMap
      })
      startTransition(SlideDirection.Forward)
      goToPreferencesQuestionnaire({
        cameFrom: options?.cameFrom ?? EXPERT_REVIEW_CAME_FROM.PREFERENCES_REVIEW
      })
    },
    [goToPreferencesQuestionnaire, questionnaireMap, startTransition]
  )

  /**
   * Park cursor at type 0 / question 0.
   * AllDone keeps cursor to avoid a flash before leave — reset on leave / exit / clear.
   */
  function resetCursorToStart() {
    dispatch({ type: PreferencesSessionActionType.ResetCursor })
  }

  /** Discard uncommitted draft and reset cursor — hub / menu leave. */
  function clearSession() {
    discardDraftAnswers()
    resetCursorToStart()
  }

  /** Hub resume — mark that full preferences review was opened this session. */
  const markReachedReview = useCallback(() => {
    dispatch({ type: PreferencesSessionActionType.MarkReachedReview })
  }, [])

  /** After successful POST — use answersVersion from the mutation vars (in-flight-safe). */
  const markSubmittedAnswersVersion = useCallback((answersVersion: number) => {
    dispatch({
      type: PreferencesSessionActionType.MarkSubmittedAnswers,
      lastSubmittedAnswersVersion: answersVersion
    })
  }, [])

  return {
    draftAnswers,
    committedAnswers,
    committedAnswersVersion,
    lastSubmittedAnswersVersion,
    isAnswersVersionOutOfSync,
    hasReachedReviewOnce,
    markReachedReview,
    markSubmittedAnswersVersion,
    preferenceTypeIndex,
    questionIndex,
    answerQuestion,
    updateDraftAnswer,
    clearAnswer,
    finishFromDraftAnswers,
    leaveQuestionnaire,
    cancelAnswering,
    returnToReview,
    beginEditOne,
    beginEditAll,
    clearSession,
    getVisibleQuestionsForPreferenceType,
    currentPreferenceType,
    currentQuestion,
    visibleQuestionsForCurrentType,
    slideDirection,
    goToQuestion
  }
}
